/**
 * FTT conjoint recognition-memory items — a POOL per article.
 *
 * Each tested article draws a constrained-random subset (see sampleMemoryProbes)
 * so the participant cannot learn a fixed pattern (e.g. "the number item is
 * always the real one"). Every item carries TWO independent properties:
 *
 *   kind     — the conjoint-recognition role (5-way)
 *   present  — ground truth: did this actually appear in the article?
 *
 *   kind                 present  "yes" answer means…
 *   ───────────────────  ───────  ────────────────────────────────────────────
 *   verbatim-target      true     VERBATIM HIT      — recognised the exact detail
 *   verbatim-distractor  false    VERBATIM FALSE ALARM — failed to reject an
 *                                 *altered* exact detail (the recollection-
 *                                 rejection probe; rejecting it = strong verbatim)
 *   gist-target          true     GIST HIT          — accepted a faithful paraphrase
 *   gist-lure            false    GIST FALSE MEMORY — accepted a meaning-consistent
 *                                 statement that was never stated
 *   foil                 false    GUESS/INATTENTION — accepted an off-meaning,
 *                                 absent statement (guessing baseline)
 *
 * This crosses TRACE (verbatim vs gist) × VERACITY (present vs absent), so the
 * test measures verbatim and gist discrimination *under both veracities* — not
 * just "accept the true detail". Scoring is by per-trace hit and false-alarm
 * RATES (see scoreMemoryProbes), which are robust to the variable item counts a
 * random draw produces.
 *
 * Items are about the study's OWN synthetic articles and were vetted line-by-line
 * against each article body. Verbatim items use a real number wherever the
 * article contains one (numbers are the purest verbatim trace). Some false-news
 * articles have no salient figure, so their verbatim items are exact non-numeric
 * details (named agency / town / drug / book) — not invented numbers.
 *
 * NOTE for the research team: three foils in the previous (fixed-quartet) version
 * were mislabeled — d2/d3/d4 each used the *reality-correct* rebuttal as a foil,
 * but each article actually CONTAINS that rebuttal (officials' denial, recounts
 * matching, doctors calling the drug safe), so those statements were "present",
 * not absent. They are corrected here (the foils now assert the opposite, which
 * is genuinely absent from the article).
 */

export type ProbeKind =
  | 'verbatim-target'
  | 'verbatim-distractor'
  | 'gist-target'
  | 'gist-lure'
  | 'foil';

export interface MemoryProbeItem {
  id: string;
  text: string;
  kind: ProbeKind;
  /** Ground truth: did this statement actually appear in the article? */
  present: boolean;
}

export const isVerbatim = (k: ProbeKind) => k === 'verbatim-target' || k === 'verbatim-distractor';
export const isGist = (k: ProbeKind) => k === 'gist-target' || k === 'gist-lure';

// Pools — 6 items/article: 2 verbatim (1 target present, 1 distractor altered),
// 2 gist (1 target present, 1 lure absent), 1 extra verbatim-target, 1 foil.
// (Where an article supports two genuine exact details, the extra verbatim-target
// is a second real detail; this also lets a draw occasionally be "mostly true".)
export const MEMORY_PROBES: Record<string, MemoryProbeItem[]> = {
  // ── A: TRUE · Pro-Left ──────────────────────────────────────────────
  a1: [ // climate — hottest year on record
    { id: 'a1_vt1', kind: 'verbatim-target',     present: true,  text: 'Atmospheric CO₂ had reached about 420 parts per million.' },
    { id: 'a1_vt2', kind: 'verbatim-target',     present: true,  text: 'The year included a streak of 15 consecutive record-hot months.' },
    { id: 'a1_vd',  kind: 'verbatim-distractor', present: false, text: 'The year averaged about 2.5°C above pre-industrial levels.' },
    { id: 'a1_gt',  kind: 'gist-target',         present: true,  text: 'The bottom line was that the planet is warming to record levels.' },
    { id: 'a1_gl',  kind: 'gist-lure',           present: false, text: 'It reported that the 1.5°C target had now been permanently breached.' },
    { id: 'a1_f',   kind: 'foil',                present: false, text: 'Global temperatures had plateaued over the past decade.' },
  ],
  a2: [ // immigration — CBO economic benefit
    { id: 'a2_vt1', kind: 'verbatim-target',     present: true,  text: 'The CBO projected roughly a $0.9 trillion reduction in federal deficits.' },
    { id: 'a2_vt2', kind: 'verbatim-target',     present: true,  text: 'The CBO estimated the surge would add about $8.9 trillion to GDP over a decade.' },
    { id: 'a2_vd',  kind: 'verbatim-distractor', present: false, text: 'The CBO projected about a $4.9 trillion boost to GDP.' },
    { id: 'a2_gt',  kind: 'gist-target',         present: true,  text: 'The overall message was that the immigration surge benefits the U.S. economy.' },
    { id: 'a2_gl',  kind: 'gist-lure',           present: false, text: 'Immigrants were filling millions of unfilled jobs in healthcare and construction.' },
    { id: 'a2_f',   kind: 'foil',                present: false, text: 'The immigration surge would shrink the U.S. economy over the next decade.' },
  ],
  a3: [ // guns — leading cause of death for children
    { id: 'a3_vt1', kind: 'verbatim-target',     present: true,  text: 'There were 4,733 firearm deaths versus 4,048 motor-vehicle deaths.' },
    { id: 'a3_vt2', kind: 'verbatim-target',     present: true,  text: 'The figures covered children and teens ages 1 to 19.' },
    { id: 'a3_vd',  kind: 'verbatim-distractor', present: false, text: 'There were about 6,700 firearm deaths among children that year.' },
    { id: 'a3_gt',  kind: 'gist-target',         present: true,  text: 'The takeaway was that guns are now the leading cause of death for U.S. children.' },
    { id: 'a3_gl',  kind: 'gist-lure',           present: false, text: 'Firearm deaths among children had roughly doubled since 2010.' },
    { id: 'a3_f',   kind: 'foil',                present: false, text: 'Car accidents remain the leading cause of death for U.S. children.' },
  ],
  a4: [ // jan 6 — officers assaulted
    { id: 'a4_vt1', kind: 'verbatim-target',     present: true,  text: 'About 129 people were charged with using a deadly or dangerous weapon.' },
    { id: 'a4_vt2', kind: 'verbatim-target',     present: true,  text: 'More than 140 officers were assaulted during the attack.' },
    { id: 'a4_vd',  kind: 'verbatim-distractor', present: false, text: 'More than 240 officers were assaulted during the attack.' },
    { id: 'a4_gt',  kind: 'gist-target',         present: true,  text: 'The article portrayed Jan. 6 as a violent attack in which many officers were assaulted.' },
    { id: 'a4_gl',  kind: 'gist-lure',           present: false, text: 'Several police officers died from injuries sustained during the attack.' },
    { id: 'a4_f',   kind: 'foil',                present: false, text: 'The day was described as a largely peaceful demonstration.' },
  ],
  // ── B: TRUE · Pro-Right ─────────────────────────────────────────────
  b1: [ // economy — inflation 9.1%
    { id: 'b1_vt1', kind: 'verbatim-target',     present: true,  text: 'Inflation peaked at 9.1%.' },
    { id: 'b1_vt2', kind: 'verbatim-target',     present: true,  text: 'It was described as the largest annual increase since 1981.' },
    { id: 'b1_vd',  kind: 'verbatim-distractor', present: false, text: 'Inflation peaked at 7.1%.' },
    { id: 'b1_gt',  kind: 'gist-target',         present: true,  text: 'The point was that inflation hit its worst level in about 40 years under Biden.' },
    { id: 'b1_gl',  kind: 'gist-lure',           present: false, text: 'Gasoline prices had roughly doubled over the same period.' },
    { id: 'b1_f',   kind: 'foil',                present: false, text: 'Inflation never rose above 3% during this period.' },
  ],
  b2: [ // energy — US oil & gas output
    { id: 'b2_vt1', kind: 'verbatim-target',     present: true,  text: 'U.S. output was about 23.6 million barrels of oil and gas liquids per day.' },
    { id: 'b2_vt2', kind: 'verbatim-target',     present: true,  text: 'Counting crude only, the U.S. produced about 13.6 million barrels per day.' },
    { id: 'b2_vd',  kind: 'verbatim-distractor', present: false, text: 'U.S. output was about 32.6 million barrels per day.' },
    { id: 'b2_gt',  kind: 'gist-target',         present: true,  text: 'The message was that the U.S. is now a dominant global energy producer.' },
    { id: 'b2_gl',  kind: 'gist-lure',           present: false, text: 'The U.S. had become the world\'s largest exporter of crude oil.' },
    { id: 'b2_f',   kind: 'foil',                present: false, text: 'The U.S. produces less oil and gas than Saudi Arabia alone.' },
  ],
  b3: [ // immigration — fentanyl seizures
    { id: 'b3_vt1', kind: 'verbatim-target',     present: true,  text: 'About 4,500 pounds of fentanyl were seized in two months.' },
    { id: 'b3_vt2', kind: 'verbatim-target',     present: true,  text: 'Only about 2,800 pounds had been seized in all of 2019.' },
    { id: 'b3_vd',  kind: 'verbatim-distractor', present: false, text: 'About 9,500 pounds of fentanyl were seized in two months.' },
    { id: 'b3_gt',  kind: 'gist-target',         present: true,  text: 'The takeaway was that the border drug problem had worsened sharply.' },
    { id: 'b3_gl',  kind: 'gist-lure',           present: false, text: 'Fentanyl overdose deaths had reached a record high that year.' },
    { id: 'b3_f',   kind: 'foil',                present: false, text: 'Fentanyl seizures had fallen sharply compared with 2019.' },
  ],
  b4: [ // economy — grocery prices
    { id: 'b4_vt1', kind: 'verbatim-target',     present: true,  text: 'Grocery prices rose about 20% over three years.' },
    { id: 'b4_vt2', kind: 'verbatim-target',     present: true,  text: 'Cereals and bakery products rose about 25.6%.' },
    { id: 'b4_vd',  kind: 'verbatim-distractor', present: false, text: 'Grocery prices rose about 40% over three years.' },
    { id: 'b4_gt',  kind: 'gist-target',         present: true,  text: 'The point was that food costs rose steeply during Biden\'s first years.' },
    { id: 'b4_gl',  kind: 'gist-lure',           present: false, text: 'The average family\'s grocery bill rose by more than $1,000 a year.' },
    { id: 'b4_f',   kind: 'foil',                present: false, text: 'Grocery prices were largely flat over the three-year period.' },
  ],
  // ── C: FALSE · Pro-Left ─────────────────────────────────────────────
  c1: [ // abortion — Project 2025 register pregnancies (false)
    { id: 'c1_vt1', kind: 'verbatim-target',     present: true,  text: 'The plan was described as a nearly 900-page document.' },
    { id: 'c1_vt2', kind: 'verbatim-target',     present: true,  text: 'Women would have to register with a new federal agency when pregnant.' },
    { id: 'c1_vd',  kind: 'verbatim-distractor', present: false, text: 'The plan was described as a nearly 500-page document.' },
    { id: 'c1_gt',  kind: 'gist-target',         present: true,  text: 'The thrust was that the plan would let the government track women\'s pregnancies.' },
    { id: 'c1_gl',  kind: 'gist-lure',           present: false, text: 'The plan would also ban most forms of contraception nationwide.' },
    { id: 'c1_f',   kind: 'foil',                present: false, text: 'The plan contained no provisions related to pregnancy.' },
  ],
  c2: [ // character — Vance couch memoir (false)
    { id: 'c2_vt1', kind: 'verbatim-target',     present: true,  text: 'The claim referenced Vance\'s 2016 book "Hillbilly Elegy."' },
    { id: 'c2_vt2', kind: 'verbatim-target',     present: true,  text: 'The viral posts included specific page citations.' },
    { id: 'c2_vd',  kind: 'verbatim-distractor', present: false, text: 'The claim referenced Vance\'s book "Hillbilly Highway."' },
    { id: 'c2_gt',  kind: 'gist-target',         present: true,  text: 'The thrust was that Vance had written something bizarre about a couch.' },
    { id: 'c2_gl',  kind: 'gist-lure',           present: false, text: 'The passage had been quietly removed from later printings of the book.' },
    { id: 'c2_f',   kind: 'foil',                present: false, text: 'Vance confirmed the couch passage was genuine.' },
  ],
  c3: [ // climate — Great Barrier Reef "is dead" (false), HuffPost / Al Gore
    { id: 'c3_vt1', kind: 'verbatim-target',     present: true,  text: 'The reef described stretches more than 1,400 miles.' },
    { id: 'c3_vt2', kind: 'verbatim-target',     present: true,  text: 'The article said close to 29 percent of the reef\'s shallow-water coral had died.' },
    { id: 'c3_vd',  kind: 'verbatim-distractor', present: false, text: 'The article said close to 60 percent of the reef\'s coral had died.' },
    { id: 'c3_gt',  kind: 'gist-target',         present: true,  text: 'The overall message was that the Great Barrier Reef had died from warming.' },
    { id: 'c3_gl',  kind: 'gist-lure',           present: false, text: 'Scientists agreed the reef was beyond saving and could not recover.' },
    { id: 'c3_f',   kind: 'foil',                present: false, text: 'The article was mainly about plastic pollution harming the reef.' },
  ],
  c4: [ // election — Clinton "illegitimate" (false)
    { id: 'c4_vt1', kind: 'verbatim-target',     present: true,  text: 'Clinton called Trump an "illegitimate president."' },
    { id: 'c4_vt2', kind: 'verbatim-target',     present: true,  text: 'Her claim concerned the 2016 election.' },
    { id: 'c4_vd',  kind: 'verbatim-distractor', present: false, text: 'Her claim concerned the 2020 election.' },
    { id: 'c4_gt',  kind: 'gist-target',         present: true,  text: 'The thrust was that Clinton claimed the 2016 election was not legitimate.' },
    { id: 'c4_gl',  kind: 'gist-lure',           present: false, text: 'She called for the 2016 election result to be overturned.' },
    { id: 'c4_f',   kind: 'foil',                present: false, text: 'She fully accepted the legitimacy of the 2016 election.' },
  ],
  // ── D: FALSE · Pro-Right ────────────────────────────────────────────
  d1: [ // climate — NOAA "cooked the books" (false)
    { id: 'd1_vt1', kind: 'verbatim-target',     present: true,  text: 'NOAA was the agency accused of manipulating temperature data.' },
    { id: 'd1_vt2', kind: 'verbatim-target',     present: true,  text: 'Critics pointed to a report on improperly sited temperature stations.' },
    { id: 'd1_vd',  kind: 'verbatim-distractor', present: false, text: 'NASA was the agency accused of manipulating temperature data.' },
    { id: 'd1_gt',  kind: 'gist-target',         present: true,  text: 'The message was that recorded global warming is exaggerated by tampered data.' },
    { id: 'd1_gl',  kind: 'gist-lure',           present: false, text: 'Most of the recorded warming disappears once the data is corrected.' },
    { id: 'd1_f',   kind: 'foil',                present: false, text: 'The article concluded NOAA\'s temperature data was accurate and reliable.' },
  ],
  d2: [ // immigration — Springfield pets (false)
    { id: 'd2_vt1', kind: 'verbatim-target',     present: true,  text: 'The claims were about Springfield, Ohio.' },
    { id: 'd2_vt2', kind: 'verbatim-target',     present: true,  text: 'Senator JD Vance was quoted amplifying the claims.' },
    { id: 'd2_vd',  kind: 'verbatim-distractor', present: false, text: 'The claims were about Springfield, Illinois.' },
    { id: 'd2_gt',  kind: 'gist-target',         present: true,  text: 'The thrust was that immigrants were harming residents\' pets.' },
    { id: 'd2_gl',  kind: 'gist-lure',           present: false, text: 'Local police had confirmed multiple cases of missing pets.' },
    // FIXED: the article DOES include officials' denial, so "officials found no
    // evidence" is PRESENT; the foil must assert the opposite (absent).
    { id: 'd2_f',   kind: 'foil',                present: false, text: 'Officials found clear evidence supporting the pet-eating claims.' },
  ],
  d3: [ // election — Dominion switched votes (false)
    { id: 'd3_vt1', kind: 'verbatim-target',     present: true,  text: 'About 221,000 Pennsylvania votes were said to have been switched away from Trump.' },
    { id: 'd3_vt2', kind: 'verbatim-target',     present: true,  text: 'The claim alleged 2.7 million Trump votes were deleted nationwide.' },
    { id: 'd3_vd',  kind: 'verbatim-distractor', present: false, text: 'About 521,000 Pennsylvania votes were said to have been switched.' },
    { id: 'd3_gt',  kind: 'gist-target',         present: true,  text: 'The message was that voting machines had rigged the election against Trump.' },
    { id: 'd3_gl',  kind: 'gist-lure',           present: false, text: 'The same voting software was used in every swing state.' },
    // FIXED: the article DOES report that hand recounts matched the machine
    // tallies, so "audits found machines accurate" is PRESENT; foil now asserts
    // officials confirmed the switching (absent — they rejected it).
    { id: 'd3_f',   kind: 'foil',                present: false, text: 'Election officials confirmed the machines had switched votes.' },
  ],
  d4: [ // health — Tylenol & autism (false)
    { id: 'd4_vt1', kind: 'verbatim-target',     present: true,  text: 'The drug named was Tylenol (acetaminophen).' },
    { id: 'd4_vt2', kind: 'verbatim-target',     present: true,  text: 'Officials urged pregnant women to "fight like hell" not to take it.' },
    { id: 'd4_vd',  kind: 'verbatim-distractor', present: false, text: 'The drug named was ibuprofen (Advil).' },
    { id: 'd4_gt',  kind: 'gist-target',         present: true,  text: 'The message was that a common pregnancy pain reliever causes autism.' },
    { id: 'd4_gl',  kind: 'gist-lure',           present: false, text: 'Pregnant women were advised to stop taking the drug entirely.' },
    // FIXED: the article DOES note doctors consider the drug safe in pregnancy,
    // so "medical groups recommended it as safe" is PRESENT; foil now asserts
    // medical groups endorsed the autism claim (absent — they urged caution).
    { id: 'd4_f',   kind: 'foil',                present: false, text: 'Major medical groups said the drug definitely causes autism.' },
  ],
};

// Deterministic-enough shuffle (Math.random is fine in-app; draw order need not
// be reproducible — the raw items + answers are stored for offline scoring).
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickOne<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Draw a constrained-random subset of N probes for an article:
 *  - guarantees ≥1 verbatim-TARGET and ≥1 gist-TARGET (so the verbatim and gist
 *    HIT rates are always defined for every tested article — not just "some
 *    verbatim/gist item")
 *  - fills the remaining slots at random from the rest of the pool, which holds
 *    the altered-verbatim distractor, the gist lure and the foil — so a FALSE
 *    item (and thus a false-alarm probe) almost always appears too
 *  - returns the subset shuffled (presentation order randomised)
 * Net effect per test: ≥2 present (one verbatim, one gist) + 1–2 absent, so both
 * HIT and FALSE-ALARM rates are estimable, while WHICH trace is probed as false
 * (altered detail vs gist lure) still varies draw to draw. Fully homogeneous
 * (all-true / all-false) draws are intentionally avoided — they would yield no
 * within-test discrimination contrast.
 */
export function sampleMemoryProbes(articleId: string, n = 4): MemoryProbeItem[] {
  const pool = MEMORY_PROBES[articleId] || [];
  if (pool.length <= n) return shuffle(pool);

  const verbatimTargets = pool.filter(p => p.kind === 'verbatim-target');
  const gistTargets = pool.filter(p => p.kind === 'gist-target');

  const picks: MemoryProbeItem[] = [];
  if (verbatimTargets.length) picks.push(pickOne(verbatimTargets));
  if (gistTargets.length) picks.push(pickOne(gistTargets));

  const chosen = new Set(picks.map(p => p.id));
  const remaining = shuffle(pool.filter(p => !chosen.has(p.id)));
  while (picks.length < n && remaining.length) picks.push(remaining.shift()!);

  return shuffle(picks);
}

export interface MemoryItemAnswer {
  id: string;
  kind: ProbeKind;
  present: boolean;
  answeredYes: boolean;
}

export interface MemoryScore {
  // Verbatim trace
  verbatimTargetsSeen: number;   // present verbatim items shown
  verbatimHits: number;          // "yes" to present verbatim (verbatim recognition)
  verbatimDistractorsSeen: number; // altered verbatim items shown
  verbatimFalseAlarms: number;   // "yes" to altered verbatim (failure of recollection rejection)
  // Gist trace
  gistTargetsSeen: number;       // present gist items shown
  gistHits: number;              // "yes" to present gist (gist recognition)
  gistLuresSeen: number;         // gist lures shown
  gistFalseAlarms: number;       // "yes" to gist lure (GIST FALSE MEMORY)
  // Guessing baseline
  foilsSeen: number;
  foilFalseAlarms: number;       // "yes" to off-meaning foil
}

/** Score a set of answered probes by per-trace hits and false alarms. */
export function scoreMemoryProbes(answers: MemoryItemAnswer[]): MemoryScore {
  const s: MemoryScore = {
    verbatimTargetsSeen: 0, verbatimHits: 0,
    verbatimDistractorsSeen: 0, verbatimFalseAlarms: 0,
    gistTargetsSeen: 0, gistHits: 0,
    gistLuresSeen: 0, gistFalseAlarms: 0,
    foilsSeen: 0, foilFalseAlarms: 0,
  };
  for (const a of answers) {
    switch (a.kind) {
      case 'verbatim-target':
        s.verbatimTargetsSeen++; if (a.answeredYes) s.verbatimHits++; break;
      case 'verbatim-distractor':
        s.verbatimDistractorsSeen++; if (a.answeredYes) s.verbatimFalseAlarms++; break;
      case 'gist-target':
        s.gistTargetsSeen++; if (a.answeredYes) s.gistHits++; break;
      case 'gist-lure':
        s.gistLuresSeen++; if (a.answeredYes) s.gistFalseAlarms++; break;
      case 'foil':
        s.foilsSeen++; if (a.answeredYes) s.foilFalseAlarms++; break;
    }
  }
  return s;
}
